import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_PREFIX = 'scroll_pos_';

/**
 * Saves the current page's scroll position to sessionStorage when the component
 * unmounts, and restores it on mount if a saved value exists.
 *
 * Usage: call `useScrollRestoration()` at the top of any page component.
 */
export function useScrollRestoration() {
  const location = useLocation();
  const key = `${STORAGE_PREFIX}${location.pathname}`;
  const restoredRef = useRef(false);

  // Restore on mount (after first paint so layout is stable)
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const saved = sessionStorage.getItem(key);
    if (saved !== null) {
      const y = parseInt(saved, 10);
      // Two rAFs to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: y, behavior: 'instant' as ScrollBehavior });
        });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save on unmount
  useEffect(() => {
    return () => {
      sessionStorage.setItem(key, String(window.scrollY));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
